import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { ApiError, EventDetail } from '../api';
import { eventEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, statusTone } from '../ui';
import { AccountingPanel } from './event/AccountingPanel';
import { DeliveryPanel } from './event/DeliveryPanel';
import { DuplicateCard } from './event/DuplicateCard';
import { EmbedPanel } from './event/EmbedPanel';
import { PreviewPanel } from './event/PreviewPanel';
import { ExchangesPanel } from './event/ExchangesPanel';
import { FeesPanel } from './event/FeesPanel';
import { InventoryPanel } from './event/InventoryPanel';
import { OverviewPanel } from './event/OverviewPanel';
import { CategoriesPanel } from './event/CategoriesPanel';
import { CollectionsPanel } from './event/CollectionsPanel';
import { PerformancesPanel } from './event/PerformancesPanel';
import { PresalesPanel } from './event/PresalesPanel';
import { PricingPanel } from './event/PricingPanel';
import { QuotePanel } from './event/QuotePanel';
import { Tabs } from './event/shared';
import { TicketsPanel } from './event/TicketsPanel';
import { TimedEntryPanel } from './event/TimedEntryPanel';
import { SponsorsPanel } from './event/SponsorsPanel';
import { SurveysPanel } from './event/SurveysPanel';
import { ContentPanel } from './event/ContentPanel';
import { dateWords } from '../terms';

/**
 * One event, in the order an operator sets it up: what it is, when it plays,
 * what is sold, at what price, with which fees, and whose money it is — then
 * a quote to see it all add up before publishing.
 */

type Tab = 'overview' | 'categories' | 'page' | 'performances' | 'presales' | 'tickets' | 'pricing' | 'fees' | 'delivery' | 'exchanges' | 'accounting' | 'inventory' | 'entry-times' | 'sponsors' | 'surveys' | 'embed' | 'preview' | 'quote';

export interface PanelProps {
  detail: EventDetail;
  refresh: () => void;
  onError: (error: ApiError) => void;
  can: (permission: string) => boolean;
}

export default function EventPage() {
  const { id = '' } = useParams();
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery<EventDetail, ApiError>({ queryKey: ['event', id], queryFn: () => eventEndpoints.detail(id) });
  const refresh = useCallback(() => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['event', id] });
  }, [queryClient, id]);
  const onError = useCallback((cause: ApiError) => setError(cause.message), []);

  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  if (detail.error) return <ErrorNote>{detail.error.message}</ErrorNote>;
  if (!detail.data) return <p className="text-sm text-ink-400">Loading…</p>;

  const data = detail.data;
  const props: PanelProps = { detail: data, refresh, onError, can };

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div className="flex flex-wrap items-baseline gap-3">
        <Link to="/events" className="text-sm text-accent underline-offset-2 hover:underline">
          ← Events
        </Link>
        <h1 className="text-lg font-semibold text-ink-900">{data.event.title}</h1>
        <span className="text-sm text-ink-400">{data.venue.name}</span>
        <Badge tone={statusTone(data.event.status)}>{data.event.status}</Badge>
        <span className="text-xs text-ink-400">{data.event.accounting_mode.replaceAll('_', ' ').toLowerCase()}</span>
        {can('events.manage') && !copying ? (
          <Button className="ml-auto" onClick={() => setCopying(true)}>
            Copy event
          </Button>
        ) : null}
      </div>

      {copying ? <DuplicateCard detail={data} onError={onError} onClose={() => setCopying(false)} /> : null}

      <Tabs<Tab>
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'categories', label: 'Grouping' },
          { id: 'page', label: 'Page' },
          { id: 'performances', label: `${dateWords().Many}`, badge: data.performances.length },
          { id: 'presales', label: 'Presales' },
          { id: 'tickets', label: 'Tickets & zones', badge: data.ticket_types.length + data.price_zones.length },
          { id: 'pricing', label: 'Pricing', badge: data.active_price_table ? `v${data.active_price_table.version}` : 'none' },
          { id: 'fees', label: 'Fees & taxes', badge: data.fee_rules.length + data.tax_rules.length },
          { id: 'delivery', label: 'Delivery' },
          { id: 'exchanges', label: 'Exchanges & refunds' },
          { id: 'accounting', label: 'Accounting' },
          { id: 'inventory', label: 'Inventory' },
          ...(data.ticket_types.some((type) => type.timed_entry) ? [{ id: 'entry-times' as const, label: 'Entry times' }] : []),
          { id: 'sponsors', label: 'Sponsors' },
          { id: 'surveys', label: 'Surveys' },
          { id: 'embed', label: 'Embed' },
          { id: 'preview', label: 'Preview' },
          { id: 'quote', label: 'Quote' },
        ]}
      />

      {tab === 'overview' ? <OverviewPanel {...props} /> : null}
      {tab === 'categories' ? (
        <div className="space-y-4">
          <CategoriesPanel detail={data} onError={onError} />
          <CollectionsPanel detail={data} onError={onError} />
        </div>
      ) : null}
      {tab === 'page' ? <ContentPanel {...props} /> : null}
      {tab === 'performances' ? <PerformancesPanel {...props} /> : null}
      {tab === 'presales' ? <PresalesPanel {...props} /> : null}
      {tab === 'tickets' ? <TicketsPanel {...props} /> : null}
      {tab === 'pricing' ? <PricingPanel {...props} /> : null}
      {tab === 'fees' ? <FeesPanel {...props} /> : null}
      {tab === 'delivery' ? <DeliveryPanel {...props} /> : null}
      {tab === 'exchanges' ? <ExchangesPanel {...props} /> : null}
      {tab === 'accounting' ? <AccountingPanel {...props} /> : null}
      {tab === 'inventory' ? <InventoryPanel {...props} /> : null}
      {tab === 'entry-times' ? <TimedEntryPanel {...props} /> : null}
      {tab === 'sponsors' ? <SponsorsPanel {...props} /> : null}
      {tab === 'surveys' ? <SurveysPanel {...props} /> : null}
      {tab === 'embed' ? <EmbedPanel {...props} /> : null}
      {tab === 'preview' ? <PreviewPanel {...props} /> : null}
      {tab === 'quote' ? <QuotePanel {...props} /> : null}
    </div>
  );
}
